import { useT } from '@planetra/i18n';
import { NavRail, NavRailItem, NavRailItemContent } from '@planetra/ui';
import {
  FolderOpen,
  LayoutDashboard,
  LayoutGrid,
  Map as MapIcon,
  SquareDashedMousePointer,
  UserCog,
} from 'lucide-react';
import { Link, matchPath, useLocation } from 'react-router';
import {
  DASHBOARD_ROUTE,
  PROJECT_ROUTE,
  REPORT_ROUTE,
  ROUTES,
  dashboardPath,
  projectPath,
} from '../app/routes';
import { useSelectedOrganisation } from '../session';

/**
 * The portal's primary navigation (P1-MVP-07), as the Brand DNA's workspace
 * concept draws it: a charcoal rail on the left, icons only so the map keeps
 * its width, the current section on forest with a mint bar.
 *
 * "Map workspace" exists only inside a project — it has no meaning outside
 * one — and points at the project the planner is in, including from one of
 * its reports. The council's dashboard (P1-WEB-38) sits under it, for the
 * same project and for council members only.
 */
export function PortalNav() {
  const { t } = useT();
  const { pathname } = useLocation();

  const council = useSelectedOrganisation()?.role === 'council';

  const onDashboard = matchPath(DASHBOARD_ROUTE, pathname);
  const inProject =
    matchPath(REPORT_ROUTE, pathname) ??
    onDashboard ??
    matchPath({ path: PROJECT_ROUTE, end: true }, pathname);
  const projectId = inProject?.params.projectId;
  const onWorkspace = matchPath({ path: PROJECT_ROUTE, end: true }, pathname) !== null;

  const items = [
    { key: 'overview', to: ROUTES.root, icon: LayoutGrid, current: pathname === ROUTES.root },
    {
      key: 'projects',
      to: ROUTES.projects,
      icon: FolderOpen,
      current: pathname === ROUTES.projects,
    },
    { key: 'tools', to: ROUTES.tools, icon: SquareDashedMousePointer, current: false },
    ...(projectId === undefined
      ? []
      : [{ key: 'workspace', to: projectPath(projectId), icon: MapIcon, current: onWorkspace }]),
    ...(projectId === undefined || !council
      ? []
      : [
          {
            key: 'dashboard',
            to: dashboardPath(projectId),
            icon: LayoutDashboard,
            current: onDashboard !== null,
          },
        ]),
  ] as const;

  return (
    <div className="hidden w-16 shrink-0 flex-col justify-between border-r border-line bg-canvas md:flex">
      <NavRail label={t('common:nav.label')} compact>
        {items.map((item) => (
          <NavRailItem
            key={item.key}
            asChild
            current={item.current}
            icon={item.icon}
            label={t(`common:nav.${item.key}`)}
          >
            <Link to={item.to}>
              <NavRailItemContent
                icon={item.icon}
                label={t(`common:nav.${item.key}`)}
                current={item.current}
              />
            </Link>
          </NavRailItem>
        ))}
      </NavRail>
      <NavRail label={t('common:nav.account')} compact>
        <NavRailItem
          asChild
          current={pathname === ROUTES.accountSessions}
          icon={UserCog}
          label={t('common:nav.account')}
        >
          <Link to={ROUTES.accountSessions}>
            <NavRailItemContent
              icon={UserCog}
              label={t('common:nav.account')}
              current={pathname === ROUTES.accountSessions}
            />
          </Link>
        </NavRailItem>
      </NavRail>
    </div>
  );
}
